import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, CheckCircle2, Lock } from 'lucide-react';
import { useBranding } from '@/lib/branding';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetupPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const { organisationName } = useBranding();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/setup-password`, { token, new_password: password });
      setDone(true);
      toast.success('Password created!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set password');
    }
    setLoading(false);
  };

  if (!token) return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30">
      <Card className="max-w-md"><CardContent className="py-8 text-center">
        <Shield className="h-12 w-12 mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-bold">Invalid Link</h2>
        <p className="text-muted-foreground mt-2">No setup token found. Please use the link from your email.</p>
      </CardContent></Card>
    </div>
  );

  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30">
      <Card className="max-w-md"><CardContent className="py-8 text-center">
        <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
        <h2 className="text-xl font-bold">Password Created!</h2>
        <p className="text-muted-foreground mt-2">Your password has been set. You can now log in.</p>
        <Button className="mt-4" onClick={() => navigate('/login')}>Go to Login</Button>
      </CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Lock className="h-10 w-10 mx-auto text-primary mb-2" />
          <CardTitle>Set Up Your Password</CardTitle>
          <CardDescription>Create a password for your {organisationName} account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>New Password</Label><Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Minimum 8 characters" required minLength={8} data-testid="setup-password" /></div>
            <div><Label>Confirm Password</Label><Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Confirm your password" required data-testid="setup-confirm" /></div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="setup-submit">{loading ? 'Creating...' : 'Create Password'}</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
